import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  type?: 'warning' | 'danger' | 'info';
  isLoading?: boolean;
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  type = 'warning',
  isLoading = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  const iconColors = {
    warning: 'text-yellow-600',
    danger: 'text-red-600',
    info: 'text-blue-600',
  };
  const confirmButtonVariants = {
    warning: 'default' as const,
    danger: 'destructive' as const,
    info: 'default' as const,
  };

  return (
    <Dialog open={isOpen} onOpenChange={isLoading ? undefined : onCancel}>
      <DialogContent className="sm:max-w-md overflow-hidden min-w-0">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={cn('p-2 rounded-full bg-gray-100', iconColors[type])}
            >
              <AlertTriangle size={20} />
            </div>
            <DialogTitle>{title || t('confirmDialog.title')}</DialogTitle>
          </div>
        </DialogHeader>
        {/* Body 内容区域 */}
        <div className="px-6 py-4">
          <DialogDescription className="text-sm leading-relaxed text-gray-500">
            {message}
          </DialogDescription>
        </div>
        <DialogFooter className="flex-row justify-end gap-3">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {cancelText || t('common.cancel')}
          </Button>
          <Button
            variant={confirmButtonVariants[type]}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmText || t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
